import config from "../config"

const API_BASE_URL = config.apiUrl || "http://localhost:8080"

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @param {string} userData.email - User's email address
 * @param {string} userData.username - User's username
 * @param {string} userData.password - User's password
 * @param {string} [userData.first_name] - User's first name
 * @param {string} [userData.last_name] - User's last name
 * @param {string} [userData.phone_number] - User's phone number
 * @param {string} [userData.recaptcha_token] - reCAPTCHA v3 token (see utilities/recaptcha.js); omitted when unavailable
 * @returns {Promise<Object>} Registration response
 */
export const registerUser = async (userData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/v1/user/register`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            // platform set here (not left to callers) so every web
            // registration is tagged, regardless of call site -- drives the
            // sysop signup advisory email's platform column.
            body: JSON.stringify({ ...userData, platform: "web" }),
        })

        const data = await response.json()

        if (!response.ok) {
            // Create an error object that matches the expected format
            const error = new Error(data.message || "Registration failed")
            error.response = { data }
            throw error
        }

        return data
    } catch (error) {
        // If it's a network error or other fetch error
        if (!error.response) {
            console.error("Network error during registration:", error)
            const networkError = new Error(
                "Unable to connect to the server. Please check your internet connection and try again.",
            )
            networkError.response = { data: { message: "Network error" } }
            throw networkError
        }

        // Re-throw API errors as-is
        throw error
    }
}

/**
 * Get user by ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} User data
 */
export const getUserById = async (userId) => {
    try {
        if (!userId) {
            throw new Error("User ID is required")
        }

        const token = localStorage.getItem("chaperon_auth_token")

        if (!token) {
            throw new Error("Authentication token not found")
        }

        const response = await fetch(`${API_BASE_URL}/v1/user/${userId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        })

        const data = await response.json()

        if (!response.ok) {
            const error = new Error(data.message || "Failed to fetch user data")
            error.response = { data }
            throw error
        }

        return data
    } catch (error) {
        if (!error.response) {
            console.error("Network error while fetching user:", error)
            const networkError = new Error(
                "Unable to connect to the server. Please check your internet connection and try again.",
            )
            networkError.response = { data: { message: "Network error" } }
            throw networkError
        }

        throw error
    }
}

/**
 * Get user by username
 * @param {string} username - Username to look up
 * @returns {Promise<Object>} User data, or throws with status 404 if not found
 */
export const getUserByUsername = async (username) => {
    try {
        if (!username) {
            throw new Error("Username is required")
        }

        const token = localStorage.getItem("chaperon_auth_token")

        if (!token) {
            throw new Error("Authentication token not found")
        }

        const response = await fetch(
            `${API_BASE_URL}/v1/user/username/${encodeURIComponent(username)}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            },
        )

        const data = await response.json()

        if (!response.ok) {
            const error = new Error(data.message || "Failed to fetch user data")
            error.status = response.status
            error.response = { data }
            throw error
        }

        return data
    } catch (error) {
        if (!error.response) {
            console.error(
                "Network error while fetching user by username:",
                error,
            )
            const networkError = new Error(
                "Unable to connect to the server. Please check your internet connection and try again.",
            )
            networkError.response = { data: { message: "Network error" } }
            throw networkError
        }

        throw error
    }
}

/**
 * Get user by email address
 * @param {string} email - Email address to look up
 * @returns {Promise<Object>} User data, or throws with status 404 if not found
 */
export const getUserByEmail = async (email) => {
    try {
        if (!email) {
            throw new Error("Email is required")
        }

        const token = localStorage.getItem("chaperon_auth_token")

        if (!token) {
            throw new Error("Authentication token not found")
        }

        const response = await fetch(
            `${API_BASE_URL}/v1/user/email/${encodeURIComponent(email)}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            },
        )

        const data = await response.json()

        if (!response.ok) {
            const error = new Error(data.message || "Failed to fetch user data")
            error.status = response.status
            error.response = { data }
            throw error
        }

        return data
    } catch (error) {
        if (!error.response) {
            console.error("Network error while fetching user by email:", error)
            const networkError = new Error(
                "Unable to connect to the server. Please check your internet connection and try again.",
            )
            networkError.response = { data: { message: "Network error" } }
            throw networkError
        }

        throw error
    }
}

/**
 * Get the authenticated user's registered push notification device tokens.
 * Used to detect whether the current user is also using the mobile app
 * (which manages its own Firebase collective sync), so the web app can
 * avoid overwriting that data with browser-derived location.
 * @returns {Promise<Object>} Response with { success: true, data: Array }
 */
export const getUserDeviceTokens = async () => {
    try {
        const token = localStorage.getItem("chaperon_auth_token")

        if (!token) {
            throw new Error("Authentication token not found")
        }

        const response = await fetch(`${API_BASE_URL}/v1/user/device-tokens`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        })

        const data = await response.json()

        if (!response.ok) {
            const error = new Error(
                data.message || "Failed to fetch device tokens",
            )
            error.status = response.status
            error.response = { data }
            throw error
        }

        return data
    } catch (error) {
        if (!error.response) {
            console.error(
                "Network error while fetching device tokens:",
                error,
            )
            const networkError = new Error(
                "Unable to connect to the server. Please check your internet connection and try again.",
            )
            networkError.response = { data: { message: "Network error" } }
            throw networkError
        }

        throw error
    }
}

/**
 * Resolve a username or email address to a user id, scoped to a community the
 * caller manages.
 *
 * Uses GET /v1/community/:id/resolve-invitee rather than the generic
 * /v1/user/username|email endpoints, which are self-only (a chief adding
 * someone else can't use them). The community-scoped endpoint authorizes the
 * caller as an owner/access-level chief and returns only a minimal record
 * (id + name + avatar, never email/phone/etc.). An unknown identifier comes
 * back as `{ found: false }` with a 200, not a 404.
 *
 * @param {number|string} communityId - The community being invited into
 * @param {string} identifier - A username or email address
 * @returns {Promise<{ userId: number|null, notFound: boolean }>} No email is
 *   returned -- for an existing user, sendInvitation resolves the address from
 *   `recipient_user_id` server-side; for an unknown email, the caller already
 *   has the typed address.
 */
export const lookupUserByIdentifier = async (communityId, identifier) => {
    // Validate identifier before using string methods
    if (typeof identifier !== "string" || identifier.trim() === "") {
        const error = new Error("Username or email is required")
        error.status = 400
        error.response = {
            data: { message: "Username or email is required" },
        }
        throw error
    }

    if (communityId === undefined || communityId === null || communityId === "") {
        const error = new Error("A community is required to look up a user")
        error.status = 400
        error.response = {
            data: { message: "A community is required to look up a user" },
        }
        throw error
    }

    const normalizedIdentifier = identifier.trim()
    const token = localStorage.getItem("chaperon_auth_token")

    if (!token) {
        throw new Error("Authentication token not found")
    }

    let response
    try {
        response = await fetch(
            `${API_BASE_URL}/v1/community/${encodeURIComponent(
                communityId,
            )}/resolve-invitee?identifier=${encodeURIComponent(
                normalizedIdentifier,
            )}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            },
        )
    } catch (networkError) {
        console.error("Network error while resolving user:", networkError)
        const err = new Error(
            "Unable to connect to the server. Please check your internet connection and try again.",
        )
        err.response = { data: { message: "Network error" } }
        throw err
    }

    const data = await response.json()

    if (!response.ok) {
        const error = new Error(data.message || "Failed to look up user")
        error.status = response.status
        error.response = { data }
        throw error
    }

    if (!data.found || !data.user) {
        return { userId: null, notFound: true }
    }

    return { userId: data.user.id || null, notFound: false }
}

/**
 * Request a password reset email. Public endpoint; the API responds with the
 * same success message whether or not the account exists.
 * @param {string} identifier - Email address or username
 * @param {string|null} [recaptchaToken] - reCAPTCHA v3 token (see utilities/recaptcha.js); omitted when unavailable
 * @returns {Promise<Object>} API response
 */
export const requestPasswordReset = async (identifier, recaptchaToken) => {
    const response = await fetch(
        `${API_BASE_URL}/v1/user/password/reset-request`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                identifier,
                recaptcha_token: recaptchaToken || undefined,
            }),
        },
    )
    const data = await response.json()
    if (!response.ok) {
        throw new Error(data.message || "Failed to request password reset")
    }
    return data
}

/**
 * Complete a password reset using the token from the reset email.
 * @param {string} token - Reset token from the emailed link
 * @param {string} password - New password (min 8 characters)
 * @returns {Promise<Object>} API response
 */
export const resetPassword = async (token, password) => {
    const response = await fetch(`${API_BASE_URL}/v1/user/password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
    })
    const data = await response.json()
    if (!response.ok) {
        throw new Error(data.message || "Failed to reset password")
    }
    return data
}

/**
 * Check if user is authenticated
 * @returns {boolean} Authentication status
 */
export const isAuthenticated = () => {
    const token = localStorage.getItem("chaperon_auth_token")
    return !!token
}

/**
 * Logout user by clearing stored token
 */
export const logout = () => {
    localStorage.removeItem("chaperon_auth_token")
}

/**
 * Ends this browser's session: clears the token and cached user, then tells
 * useAuth listeners (same tab) so the UI switches to the signed-out state.
 * Callers navigate afterward.
 */
export const signOutLocally = () => {
    logout()
    localStorage.removeItem("chaperon_user_data")
    window.dispatchEvent(new Event("authChange"))
}

/**
 * Shared request path for the authenticated calls below: attaches the bearer
 * token, parses the JSON body and throws an Error carrying `status` and
 * `response.data` on a non-2xx reply, or a friendly one on a network failure.
 * @param {string} path - API path beginning with /v1
 * @param {Object} [options] - fetch options (headers are merged)
 * @returns {Promise<Object>} Parsed JSON body
 */
const authedRequest = async (path, { headers, ...options } = {}) => {
    const token = localStorage.getItem("chaperon_auth_token")

    if (!token) {
        throw new Error("Authentication token not found")
    }

    let response
    try {
        response = await fetch(`${API_BASE_URL}${path}`, {
            ...options,
            headers: { Authorization: `Bearer ${token}`, ...headers },
        })
    } catch (networkError) {
        console.error("Network error:", networkError)
        const error = new Error(
            "Unable to connect to the server. Please check your internet connection and try again.",
        )
        error.response = { data: { message: "Network error" } }
        throw error
    }

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
        const error = new Error(data.message || "Request failed")
        error.status = response.status
        error.response = { data }
        throw error
    }

    return data
}

/**
 * Rename one of the caller's registered devices.
 * @param {number|string} deviceId - user_device_token row id
 * @param {string} deviceName - New display name
 * @returns {Promise<Object>} API response
 */
export const renameDeviceToken = (deviceId, deviceName) =>
    authedRequest(`/v1/user/device-token/${encodeURIComponent(deviceId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_name: deviceName }),
    })

/**
 * Remove one of the caller's registered devices (stops its push notifications).
 * @param {number|string} deviceId - user_device_token row id
 * @returns {Promise<Object>} API response
 */
export const removeDeviceToken = (deviceId) =>
    authedRequest(`/v1/user/device-token/${encodeURIComponent(deviceId)}`, {
        method: "DELETE",
    })

/**
 * Upload a new profile picture (JPEG, PNG or WebP, up to 5 MB). The API also
 * saves the resulting URL to the user's profile.
 * @param {File} file - Image file
 * @returns {Promise<Object>} Response with { status: "success", url }
 */
export const uploadAvatar = (file) => {
    const body = new FormData()
    body.append("file", file)
    // No Content-Type header: the browser sets the multipart boundary.
    return authedRequest("/v1/upload/avatar", { method: "POST", body })
}

/**
 * Rotates the shared auth token server-side, signing this account out of
 * every device (not just this browser) -- see SECURITY_REVIEW.md item 18.
 * Meant to be wired to a single, explicit "Sign out all devices" control,
 * not the ordinary sign-out flow above (which stays local-only). Callers
 * should still clear local storage and redirect afterward, same as
 * logout() -- this only handles the server-side rotation.
 * @returns {Promise<Object>} Response with { status: "success", message }
 */
export const logoutAllDevices = async () => {
    const token = localStorage.getItem("chaperon_auth_token")

    if (!token) {
        throw new Error("Authentication token not found")
    }

    const response = await fetch(`${API_BASE_URL}/v1/user/logout/all`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    })

    const data = await response.json()

    if (!response.ok) {
        const error = new Error(
            data.message || "Failed to sign out of all devices",
        )
        error.status = response.status
        error.response = { data }
        throw error
    }

    return data
}

/**
 * Get stored authentication token
 * @returns {string|null} Auth token
 */
export const getAuthToken = () => {
    return localStorage.getItem("chaperon_auth_token")
}

/**
 * Login user with username and password
 *
 * BACKEND REQUIREMENT: This endpoint must be implemented on the backend.
 * Expected endpoint: POST /v1/user/login
 * Expected request body: { username: string, password: string }
 * Expected response: { status: "success", user: { id, email, username, token } }
 *
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.username - User's username
 * @param {string} credentials.password - User's password
 * @returns {Promise<Object>} Login response with user data and token
 */
export const loginUser = async (credentials) => {
    try {
        const response = await fetch(`${API_BASE_URL}/v1/user/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            // platform set here (not left to callers), same reasoning as
            // registerUser -- drives the sysop login advisory email's
            // platform column.
            body: JSON.stringify({ ...credentials, platform: "web" }),
        })

        const data = await response.json()

        if (!response.ok) {
            const error = new Error(data.message || "Login failed")
            error.response = { data }
            throw error
        }

        // Store the auth token in localStorage (will be replaced with HTTP-only cookie in future)
        if (data.user?.token) {
            localStorage.setItem("chaperon_auth_token", data.user.token)
        }

        return data
    } catch (error) {
        if (!error.response) {
            console.error("Network error during login:", error)
            const networkError = new Error(
                "Unable to connect to the server. Please check your internet connection and try again.",
            )
            networkError.response = { data: { message: "Network error" } }
            throw networkError
        }

        throw error
    }
}

/**
 * Persist the caller's own live coordinates to CloudSQL
 * (PATCH /v1/user/:id/location, self-only). The server stamps
 * location_updated_at = NOW(), so callers must not use this when a fresher
 * location from another client (e.g. the mobile app) already exists.
 * @param {number|string} userId - The caller's own user ID
 * @param {number} latitude
 * @param {number} longitude
 * @param {number|null} [entityId] - Compendium entity resolved for these
 *   coordinates; omitted when no place was resolved (the API then clears
 *   the stored place rather than keeping a stale one)
 * @returns {Promise<Object>} API response
 */
export const updateUserLocation = async (
    userId,
    latitude,
    longitude,
    entityId,
) => {
    const token = localStorage.getItem("chaperon_auth_token")

    if (!token) {
        throw new Error("Authentication token not found")
    }

    const response = await fetch(`${API_BASE_URL}/v1/user/${userId}/location`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            latitude,
            longitude,
            ...(entityId != null ? { entity_id: entityId } : {}),
        }),
    })

    const data = await response.json()

    if (!response.ok) {
        const error = new Error(data.message || "Failed to update location")
        error.status = response.status
        throw error
    }

    return data
}
